import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { RequireAuth } from '@sorare/core/src/routing/RequireAuth';

import { StartCommonGame } from 'components/commonGame/StartCommonGame';

import { CommonPacks } from './CommonPacks';
import type {
  CommonPacksAvailableOnboardingLeaderboardsQuery,
  CommonPacksAvailableOnboardingLeaderboardsQueryVariables,
  CommonPacksQuery,
  CommonPacksQueryVariables,
} from './__generated__/index.graphql';

const COMMON_PACKS_QUERY = gql`
  query CommonPacksQuery($sport: Sport!) {
    market {
      id
      ...CommonPacks_marketRoot
    }
    tokens {
      ...CommonPacksPage_tokenRoot
    }
    currentUser {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        commonGameOnboarded
      }
      commonCardPoints(sport: $sport)
      gemsBalances: inGameCurrencyBalances(
        sport: $sport
        inGameCurrencies: [COMMON_GEM]
      ) {
        amount
      }
    }
  }
  ${CommonPacks.fragments.marketRoot}
  ${CommonPacks.fragments.tokenRoot}
` as TypedDocumentNode<CommonPacksQuery, CommonPacksQueryVariables>;

const COMMON_PACK_AVAILABLE_ONBOARDING_LEADERBOARDS_QUERY = gql`
  query CommonPacksAvailableOnboardingLeaderboardsQuery($sport: Sport!) {
    currentUser {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        availableOnboardingLeaderboards(rarity: common)
      }
    }
  }
` as TypedDocumentNode<
  CommonPacksAvailableOnboardingLeaderboardsQuery,
  CommonPacksAvailableOnboardingLeaderboardsQueryVariables
>;

const CheckAvailableOnboardingLeaderboards = ({
  availableElement,
  notAvailableElement,
}: {
  availableElement: ReactNode;
  notAvailableElement: ReactNode;
}): ReactNode => {
  const { sport } = useSportContext();
  const { data, loading } = useQuery(
    COMMON_PACK_AVAILABLE_ONBOARDING_LEADERBOARDS_QUERY,
    {
      variables: {
        sport,
      },
    }
  );

  const { availableOnboardingLeaderboards } =
    data?.currentUser?.anyUserSportProfile || {};

  if (loading) {
    return <LoadingIndicator fullHeight />;
  }

  if (availableOnboardingLeaderboards) {
    return availableElement;
  }

  return notAvailableElement;
};
export const CommonPacksPage = () => {
  const { sport } = useSportContext();
  const { data, refetch } = useQuery(COMMON_PACKS_QUERY, {
    variables: {
      sport,
    },
  });
  const { market, tokens, currentUser } = data || {};

  if (currentUser?.anyUserSportProfile?.commonGameOnboarded) {
    return (
      <CommonPacks marketRoot={market} tokenRoot={tokens} refetch={refetch} />
    );
  }

  // fetch availableOnboardingLeaderboards only if user is not onboarded as it's expensive
  return (
    <RequireAuth>
      <CheckAvailableOnboardingLeaderboards
        availableElement={
          <Vertical
            center
            style={{
              height: 'calc(100vh - var(--current-stack-height))',
            }}
          >
            <StartCommonGame />
          </Vertical>
        }
        notAvailableElement={
          <CommonPacks
            marketRoot={market}
            tokenRoot={tokens}
            refetch={refetch}
          />
        }
      />
    </RequireAuth>
  );
};
